import { Subscription } from 'rxjs';
import { FormGroup, FormControl, Validators, FormArray } from '@angular/forms';

import { BocOpt, EorderProduct, Eorder, FORM_SHEET_CODE } from './eorder';

function convertNullToUndefined<T>(value: T): T | undefined {
	if (value === null) {
		return undefined;
	}
	return value;
}

export class FormProduct extends FormGroup {
	readonly product: EorderProduct;
	constructor(product: EorderProduct, form: string) {
		super({
			quantity: new FormControl(product.quantity, [Validators.required, Validators.min(0.1)]),
			steelGrade: new FormControl(product.steelGrade, Validators.required),
			thicknessValue: new FormControl(product.thicknessValue, { validators: [
				Validators.required,
				Validators.min(0.1)
			] }),
			widthValue: new FormControl(product.widthValue, { validators: [
				Validators.required,
				Validators.min(1)
			] }),
			lengthValue: new FormControl(product.lengthValue, Validators.min(1)),
			rddType: new FormControl(product.rddType || 'W', Validators.required),
			rdd: new FormControl(product.rdd, Validators.required),
			transportMode: new FormControl(product.transportMode, Validators.required),
			shipToParty: new FormControl(product.shipToParty, Validators.required),
			businessType: new FormControl(product.businessType),
			serviceLevel: new FormControl(product.serviceLevel, Validators.required),
			xcarb: new FormControl(product.xcarb),
			templateNumber: new FormControl(product.templateNumber || ''),
			a2citemNumber: new FormControl(product.a2citemNumber),
			inquiryNumber: new FormControl(product.inquiryNumber),
			productType: new FormControl(product.productType),
			configurationNumber: new FormControl(product.configurationNumber),
			isSimulationCompleted: new FormControl(product.isSimulationCompleted || '', Validators.required),
			configErrMsg: new FormControl(product.configErrMsg || ''),
			loader: new FormControl(product.loader || 'false'),
			variableFormGroup: product.variableFormGroup || new FormGroup({})
		});
		this.product = product;
		if (form === FORM_SHEET_CODE) {
			this.getFormLength().setValidators([
				Validators.min(1),
				Validators.required
			]);
		}
	}

	getA2CItemNumber() {
		return <FormControl> this.get('a2citemNumber');
	}

	getFormQuantity() {
		return <FormControl> this.get('quantity');
	}

	getFormRdd() {
		return <FormControl> this.get('rdd');
	}

	getFormRddType() {
		return <FormControl> this.get('rddType');
	}

	getFormThickness() {
		return <FormControl> this.get('thicknessValue');
	}

	getFormWidth() {
		return <FormControl> this.get('widthValue');
	}

	getFormLength() {
		return <FormControl> this.get('lengthValue');
	}

	getFormSteelGrade() {
		return <FormControl> this.get('steelGrade');
	}

	getFormBusinessType() {
		return <FormControl> this.get('businessType');
	}

	getFormTransportMode() {
		return <FormControl> this.get('transportMode');
	}

	getFormServiceLevel() {
		return <FormControl> this.get('serviceLevel');
	}

	getFormXCarb() {
		return <FormControl> this.get('xcarb');
	}

	getVariableFormGroup() {
		return <FormGroup> this.get('variableFormGroup');
	}

	getFormShipToParty() {
		return <FormControl> this.get('shipToParty');
	}

	getSimulationCompleted() {
		return <FormControl> this.get('isSimulationCompleted');
	}

	getConfigErrMsg() {
		return <FormControl> this.get('configErrMsg');
	}

	getLoader() {
		return <FormControl> this.get('loader');
	}

	getConfigurationNumber() {
		return <FormControl> this.get('configurationNumber');
	}

	getProductQuantity() {
		return <number> this.getFormQuantity().value || 0;
	}

	getProduct() {
		this.product.quantity = convertNullToUndefined(
			<number | undefined> this.getFormQuantity().value
		);
		this.product.rdd = convertNullToUndefined(
			<string | undefined> this.getFormRdd().value
		);
		this.product.shipToParty = convertNullToUndefined(
			<string | undefined> this.getFormShipToParty().value
		);
		return this.product;
	}
}

class FormProducts extends FormArray {
	// parent could decomposed into children, child keeps ref to its parent

	private readonly subscription: Subscription | undefined;
	private readonly parentProducts: FormProducts | undefined;
	constructor(parentProducts?: FormProducts) {
		super([], Validators.minLength(1));
		this.parentProducts = parentProducts;
		if (!this.parentProducts) {
			return;
		}
		this.subscription = this.valueChanges.subscribe(() => {
			if (this.parentProducts) {
				this.parentProducts.updateValueAndValidity();
			}
		});
	}

	isValid() {
		if (this.length === 0) {
			return false;
		}
		for (let i = 0; i < this.length; i++) {
			if (this.getFormProductAt(i).invalid) {
				return false;
			}
		}
		return true;
	}

	getFormProductAt(index: number) {
		return <FormProduct> this.at(index);
	}

	getFormProducts() {
		return this.controls.map(control => <FormProduct> control);
	}

	addProduct(product: EorderProduct, form: string, itemCount: number, variableFormGroups: FormGroup[]) {
		for (let i = 0; i < itemCount; i++) {
			product.variableFormGroup = variableFormGroups[i];
			this.push(new FormProduct(product, form));
		}
		return this.length - 1;
	}

	removeAllProducts() {
		this.clear();
	}

	getProducts() {
		const products: EorderProduct[] = [];
		for (let i = 0; i < this.length; i++) {
			products.push(this.getFormProductAt(i).getProduct());
		}
		return products.length === 0 ? undefined : products;
	}

	getProductCount() {
		return this.length;
	}

	onDestroy() {
		if (this.subscription) {
			this.subscription.unsubscribe();
		}
	}
}

export class FormEorder extends FormGroup {
	private lastSoldTo: string | undefined;
	private category: string | undefined;
	private subCategory: string | undefined;
	private formValue: string | undefined;

	constructor() {
		super({
			customerOrderNumber: new FormControl(undefined, Validators.compose([
				Validators.required,
				Validators.maxLength(32),
				Validators.pattern('^.*[^ ]+$')
			])),
			soldTo: new FormControl(undefined, Validators.required),
			currency: new FormControl(undefined),
			products: new FormProducts(),
		});
	}

	getAndUpdateLastBoc(newBoc: BocOpt) {
		const soldTo = this.lastSoldTo;
		this.lastSoldTo = newBoc.soldTo;
		return { soldTo };
	}

	getFormCustomerOrderNumber() {
		return <FormControl> this.get('customerOrderNumber');
	}

	getFormSoldTo() {
		return <FormControl> this.get('soldTo');
	}

	getFormProducts() {
		return <FormProducts> this.get('products');
	}

	getSubCategory() {
		return this.subCategory;
	}

	getCategory() {
		return this.category;
	}

	getFormCurrency() {
		return <FormControl> this.get('currency');
	}

	getFormValue() {
		return this.formValue;
	}

	setSubCategory(subCategory: string | undefined) {
		this.subCategory = subCategory;
	}

	setCategory(category: string | undefined) {
		this.category = category;
	}

	setFormValue(formValue: string | undefined) {
		this.formValue = formValue;
	}

	isEorderHeaderValid() {
		return this.getFormCustomerOrderNumber().valid
			&& this.getFormSoldTo().valid;
	}

	setEorder(order: Eorder) {
		const { header } = order;
		this.getFormCustomerOrderNumber().setValue(header.customerOrderNumber);
		this.lastSoldTo = header.soldTo;
		this.getFormSoldTo().setValue(header.soldTo);
		return this;
	}

	getEorder() {
		return {
			header: {
				customerOrderNumber: convertNullToUndefined(<string | undefined> this.getFormCustomerOrderNumber()
					.value),
				soldTo: convertNullToUndefined(<string | undefined> this.getFormSoldTo().value),
				currency: convertNullToUndefined(<string | undefined> this.getFormCurrency().value)
			},
			products: this.getFormProducts().getProducts()
		};
	}
}
